import { useCallback, useMemo, startTransition } from "react";
import { useSearch, useLocation } from "wouter";
import { formatSearchRange } from "@/lib/search-date-label";
import i18n from "@/i18n";
import type { SearchParams } from "@/lib/api";
import { isSameLocation } from "@/lib/historyWrite";
import { searchResultsPath } from "@/lib/search-results-path";
import { parseSearchRequest } from "@/lib/search-request-params";
import { usePublicSearchQuery } from "@/lib/regional-search";

export function formatDateChipValue(from?: string, to?: string, language?: string): string {
  return formatSearchRange(from, to, language);
}

export function useSearchFilters() {
  const browserSearch = useSearch();
  const search = usePublicSearchQuery(browserSearch);
  const [pathname, setLocation] = useLocation();
  const resultPath = searchResultsPath(pathname);

  const params = useMemo((): SearchParams => parseSearchRequest(search), [search]);

  const applyFiltersToParams = (
    sp: URLSearchParams,
    entries: Array<[string, string | string[] | number | undefined]>,
  ) => {
    sp.delete("page");
    for (const [key, value] of entries) {
      sp.delete(key);
      if (value !== undefined && value !== "" && value !== null) {
        if (Array.isArray(value)) {
          value.forEach(v => sp.append(key, v));
        } else {
          sp.set(key, String(value));
        }
      }
    }
  };

  // Skip no-op writes so rapid/repeated filter syncs don't pile up history
  // mutations toward Firefox's 100-per-10s push/replaceState throttle.
  const navigateSearch = useCallback((sp: URLSearchParams) => {
    const url = `${resultPath}?${sp.toString()}`;
    if (isSameLocation(url)) return;
    // Filter toggles re-render the whole search page (results grid + map)
    // via this URL write. Mark it a transition so the click's own render
    // (button active state) paints immediately and the heavy re-render is
    // interruptible — keeps filter interactions under the INP budget
    // (task #7081).
    startTransition(() => setLocation(url));
  }, [setLocation, resultPath]);

  const setFilter = useCallback((key: string, value: string | string[] | number | undefined) => {
    const sp = new URLSearchParams(search);
    applyFiltersToParams(sp, [[key, value]]);
    navigateSearch(sp);
  }, [search, navigateSearch]);

  const setFilters = useCallback((entries: Record<string, string | string[] | number | undefined>) => {
    const sp = new URLSearchParams(search);
    applyFiltersToParams(sp, Object.entries(entries));
    navigateSearch(sp);
  }, [search, navigateSearch]);

  const removeFilterValue = useCallback((key: string, value: string) => {
    const sp = new URLSearchParams(search);
    sp.delete("page");
    const allVals = sp.getAll(key);
    sp.delete(key);
    allVals.filter(v => v !== value).forEach(v => sp.append(key, v));
    navigateSearch(sp);
  }, [search, navigateSearch]);

  const setPage = useCallback((page: number) => {
    const sp = new URLSearchParams(search);
    if (page <= 1) sp.delete("page");
    else sp.set("page", String(page));
    navigateSearch(sp);
  }, [search, navigateSearch]);

  const clearAll = useCallback(() => {
    startTransition(() => setLocation(resultPath));
  }, [setLocation, resultPath]);

  const activeFilters = useMemo(() => {
    const filters: Array<{ key: string; label: string; value: string; isMulti: boolean }> = [];
    if (params.q) filters.push({ key: "q", label: "Search", value: params.q, isMulti: false });
    if (params.rental_type) filters.push({ key: "rental_type", label: "Type", value: params.rental_type, isMulti: false });
    if (params.location) filters.push({ key: "location", label: "Location", value: params.location, isMulti: false });
    if (params.make) filters.push({ key: "make", label: "Make", value: params.make, isMulti: false });
    // The page supplies denomination-aware presentation once its actual
    // country constraint and result currencies are known.
    if (params.price_min != null) filters.push({ key: "price_min", label: "Min Price", value: String(params.price_min), isMulti: false });
    if (params.price_max != null) filters.push({ key: "price_max", label: "Max Price", value: String(params.price_max), isMulti: false });
    if (params.passengers) filters.push({ key: "passengers", label: "Passengers", value: `${params.passengers}+`, isMulti: false });
    if (params.date_from || params.date_to) {
      filters.push({
        key: "dates",
        label: "Dates",
        value: formatDateChipValue(params.date_from, params.date_to, i18n.language),
        isMulti: false,
      });
    }
    params.car_type?.forEach(t => filters.push({ key: "car_type", label: "Type", value: t, isMulti: true }));
    params.era?.forEach(e => filters.push({ key: "era", label: "Era", value: e, isMulti: true }));
    params.features?.forEach(f => filters.push({ key: "features", label: "Feature", value: f, isMulti: true }));
    return filters;
  }, [params]);

  return { params, setFilter, setFilters, removeFilterValue, setPage, clearAll, activeFilters };
}
